import { CalendarCheck, CalendarX } from "lucide-react";
import React, { type ReactElement, type ReactNode } from "react";
import { cn } from "@ndd/ui";
import type { AppointmentDetailResponse } from "../../../../shared/api-types";
import {
  formatAppointmentDate,
  formatAppointmentTime,
  formatAppointmentTimeRange,
  shortDateLabel,
} from "../../lib/appointments";
import { Eyebrow } from "../v2/eyebrow";
import {
  CLASSIC_ONLY,
  VISIT_FACT,
  VISIT_STRIP,
  visitValueClass,
} from "./workspace-look";

// The appointment header's visit line. A module of its own, with relative
// imports, so `tsx --test` can render it.

/**
 * Classic: the long date and time, then the next-visit chip. Under the theme:
 * THIS VISIT, and NEXT VISIT reading "Not booked" in red only when none is
 * scheduled (the approved NO-799 spec; a booked next visit adds no column).
 */
export function VisitLine({
  item,
  timezone,
}: {
  item: AppointmentDetailResponse;
  timezone: string;
}): ReactElement {
  const next = item.nextOwnedVisit;

  return (
    <div className={VISIT_STRIP}>
      <p className={cn("text-sm text-muted-foreground", CLASSIC_ONLY)}>
        {formatAppointmentDate(item.startsAt, timezone)} ·{" "}
        {formatAppointmentTimeRange(item, timezone)}
      </p>
      <NextVisitChip nextOwnedVisit={next} timezone={timezone} />
      <VisitFact label="This visit">
        {shortDateLabel(item.startsAt, timezone)} ·{" "}
        {formatAppointmentTimeRange(item, timezone)}
      </VisitFact>
      {next ? null : (
        <VisitFact label="Next visit" missing>
          Not booked
        </VisitFact>
      )}
    </div>
  );
}

function VisitFact({
  label,
  missing = false,
  children,
}: {
  label: string;
  missing?: boolean;
  children: ReactNode;
}): ReactElement {
  return (
    <div className={VISIT_FACT}>
      <Eyebrow size="sm">{label}</Eyebrow>
      <span className={visitValueClass(missing)}>{children}</span>
    </div>
  );
}

/** Neutral when a visit exists; amber when none is booked — a continuity risk worth noticing, not an in-app error. */
function NextVisitChip({
  nextOwnedVisit,
  timezone,
}: {
  nextOwnedVisit: AppointmentDetailResponse["nextOwnedVisit"];
  timezone: string;
}): ReactElement {
  if (nextOwnedVisit) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border border-border bg-muted py-[3px] pl-2 pr-2.5 text-xs font-medium text-muted-foreground",
          CLASSIC_ONLY
        )}
      >
        <CalendarCheck aria-hidden="true" className="size-[13px]" />
        Next visit{" "}
        <span className="text-foreground">
          {shortDateLabel(nextOwnedVisit.startsAt, timezone)} ·{" "}
          {formatAppointmentTime(nextOwnedVisit.startsAt, timezone)}
        </span>
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border border-warning-soft bg-warning-soft py-[3px] pl-2 pr-2.5 text-xs font-medium text-warning",
        CLASSIC_ONLY
      )}
    >
      <CalendarX aria-hidden="true" className="size-[13px]" />
      No next visit scheduled
    </span>
  );
}
